import { AlertTriangle, IndianRupee, Info, Save } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Toggle from "@/components/ui/Toggle";
import { useToast } from "@/components/ui/Toast";
import { setKycFee, setWalletRates } from "@/pages/retailer/wallet/constants";
import { formatINR } from "@/pages/admin/data";
import { adminService } from "@/services/apiServices";

export default function BillingTab() {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [kycFee, setKyc] = useState("2000");
  const [creation, setCreation] = useState("500");
  const [installment, setInstallment] = useState("40");
  const [lowBalance, setLowBalance] = useState("500");
  const [minTopup, setMinTopup] = useState("500");
  const [maxTopup, setMaxTopup] = useState("100000");
  const [requireReceipt, setRequireReceipt] = useState(true);

  useEffect(() => {
    adminService
      .getSettings()
      .then((res) => {
        if (res && res.data && res.data.billingRates) {
          const {
            kycFee: k,
            mandateCreationFee: m,
            installmentCharge: i,
            lowBalanceThreshold: lb,
            minTopup: mn,
            maxTopup: mx,
            requireReceipt: rr,
          } = res.data.billingRates;
          if (k) {
            setKyc(String(k));
            setKycFee(k);
          }
          if (m !== undefined) {
            const finalM = Number(m) === 99 ? 500 : m;
            setCreation(String(finalM));
          }
          if (i !== undefined) {
            setInstallment(String(i));
          }
          if (lb !== undefined) {
            setLowBalance(String(lb));
          }
          if (mn !== undefined) {
            setMinTopup(String(mn));
          }
          if (mx !== undefined) {
            setMaxTopup(String(mx));
          }
          if (rr !== undefined) {
            setRequireReceipt(Boolean(rr));
          }
          setWalletRates(Number(m === 99 ? 500 : (m || 500)), Number(i || 40), Number(lb || 500));
        }
      })
      .catch(() => undefined);
  }, []);

  const blockingThreshold = Number(creation) || 500;

  function save() {
    setSaving(true);
    const kNum = Number(kycFee) || 2000;
    const cNum = Number(creation) || 500;
    const iNum = Number(installment) || 40;
    const lbNum = Number(lowBalance) || 500;
    const mnNum = Number(minTopup) || 500;
    const mxNum = Number(maxTopup) || 100000;

    adminService
      .updateBillingRates({
        kycFee: kNum,
        mandateCreationFee: cNum,
        installmentCharge: iNum,
        lowBalanceThreshold: lbNum,
        minTopup: mnNum,
        maxTopup: mxNum,
        requireReceipt,
      })
      .then(() => {
        setKycFee(kNum);
        setWalletRates(cNum, iNum, lbNum);
        setSaving(false);
        toast.success("Billing settings saved to database successfully");
      })
      .catch((err) => {
        setKycFee(kNum);
        setWalletRates(cNum, iNum, lbNum);
        setSaving(false);
        toast.error(err?.message || "Failed to save billing settings");
      });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Platform Charges */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Platform Charges</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Rates charged to retailers across the platform</p>

        <div className="mt-5 flex items-start gap-3 rounded-lg border border-info/30 bg-info-bg p-3.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-info">
            <Info className="h-5 w-5" />
          </span>
          <p className="text-[13px] text-ink-secondary">
            These rates apply to all retailers. Changes affect only new transactions and do not alter charges already
            applied.
          </p>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="KYC Verification Fee"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={kycFee}
            onChange={(e) => setKyc(e.target.value)}
            helper="One-time fee deducted when a retailer submits their KYC. Refunded manually if the application is rejected."
          />
          <Input
            label="Mandate Creation Charge"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={creation}
            onChange={(e) => setCreation(e.target.value)}
            helper="Deducted when a mandate is created. Not refunded automatically."
          />
          <Input
            label="Installment Charge"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={installment}
            onChange={(e) => setInstallment(e.target.value)}
            helper="Deducted automatically for each successfully collected installment. Failed installments are not charged."
          />
        </div>

        <div className="mt-5 flex items-start gap-3 rounded-lg border border-warning/40 bg-warning-bg p-3.5">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-warning">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <p className="text-[13px] text-ink-secondary">
            Lowering a rate for a promotional offer applies immediately to all retailers. Existing scheduled installments
            will be charged at the rate in effect at the time of collection.
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <Button onClick={save} loading={saving} leftIcon={<Save className="h-4 w-4" />}>
            Save Changes
          </Button>
        </div>
      </div>

      {/* Balance Thresholds */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Balance Thresholds</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Alerts shown to retailers as their balance drops</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Low Balance Warning"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={lowBalance}
            onChange={(e) => setLowBalance(e.target.value)}
            helper="Retailers see a warning banner below this balance."
          />
          <div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-page px-4 py-3">
              <span className="text-[13px] text-ink-secondary">Blocking Threshold</span>
              <span className="text-sm font-semibold text-ink">{formatINR(blockingThreshold)}</span>
            </div>
            <p className="mt-1.5 text-xs text-ink-muted">
              Mandate creation is blocked below this balance. This equals the mandate creation charge.
            </p>
          </div>
        </div>
      </div>

      {/* Top-up Settings */}
      <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Top-up Settings</h3>
        <p className="mt-0.5 text-[13px] text-ink-secondary">Limits and requirements for retailer wallet top-ups</p>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Minimum Top-up Amount"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={minTopup}
            onChange={(e) => setMinTopup(e.target.value)}
          />
          <Input
            label="Maximum Top-up Amount"
            type="number"
            prefixIcon={<IndianRupee className="h-4 w-4" />}
            value={maxTopup}
            onChange={(e) => setMaxTopup(e.target.value)}
          />
        </div>

        <div className="mt-4 border-t border-line pt-4">
          <Toggle
            label="Require receipt upload for top-up requests"
            checked={requireReceipt}
            onChange={setRequireReceipt}
          />
        </div>
      </div>
    </div>
  );
}